import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import QuestionsSettings from './QuestionsSettings';
import SectorSettings from './SectorSettings';
import AdminDashboard from './AdminDashboard';
import AdminDuplicates from './AdminDuplicates';
import GlobalStatistics from '../components/GlobalStatistics';
import UsersStatistics from '../components/UsersStatistics';
import DataHealth from '../components/DataHealth';
import SearchInsights from '../components/SearchInsights';
import Indexation from '../components/Indexation';
import AdminSidebar from '../components/AdminSidebar';
import AgentMarketplace from './picture-ai/AgentMarketplace';
import ConfigurationList from './picture-ai/ConfigurationList';
import ConfigurationEditor from './picture-ai/ConfigurationEditor';

import { API_BASE_URL } from '../config';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';

const PROJECT_SCOPED_ADMIN_PATHS = ['/admin/questions', '/admin/sectors', '/admin/duplicates'];

const AdminPage = ({ fetchWithAuth, currentUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const shellRef = useRef(null);
  const [shellHeight, setShellHeight] = useState(undefined);

  const fetchProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/columns/project`);
      const data = await response.json();
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    if (!currentUser.isadmin) {
      navigate('/');
    } else {
      if (PROJECT_SCOPED_ADMIN_PATHS.includes(location.pathname)) {
        fetchProjects();
      }
      if (location.pathname === '/admin') {
        navigate('/admin/statistics');
      }
    }
  }, [currentUser.isadmin, navigate, fetchProjects, location.pathname]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isSidebarOpen]);

  // The back-office owns the viewport below the topbar so that only its content column scrolls.
  // The topbar is rendered by the application shell, so its height is read off where this one starts.
  useLayoutEffect(() => {
    const element = shellRef.current;
    if (!element) return undefined;

    const measure = () => setShellHeight(`calc(100dvh - ${Math.round(element.getBoundingClientRect().top + window.scrollY)}px)`);

    measure();
    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
  }, []);

  if (!currentUser.isadmin) return null;

  const isProjectScoped = PROJECT_SCOPED_ADMIN_PATHS.includes(location.pathname);

  return (
    <div ref={shellRef} className="flex overflow-hidden" style={{ height: shellHeight }}>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-gray-600 bg-opacity-50 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside className={`
        fixed inset-y-0 left-0 z-40 w-64 border-r border-gray-200 bg-gray-100
        transition-transform duration-300 ease-in-out
        dark:border-gray-700 dark:bg-gray-800
        md:static md:h-full md:translate-x-0
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <AdminSidebar
          currentUser={currentUser}
          pathname={location.pathname}
          onNavigate={() => setIsSidebarOpen(false)}
        />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="shrink-0 border-b border-gray-200 p-2 dark:border-gray-700 md:hidden">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="rounded-md p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
            <span className="sr-only">{t('Open the menu')}</span>
          </button>
        </div>

        {isProjectScoped && (
          <div className="shrink-0 border-b border-gray-200 bg-white dark:bg-gray-700 dark:border-gray-700 p-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
              <Select
                value={projects?.find(p => p.value === selectedProject)}
                onChange={(option) => setSelectedProject(option?.value || '')}
                options={projects}
                isClearable
                isLoading={projectsLoading}
                isDisabled={projectsLoading}
                placeholder={projectsLoading ? t('Loading projects...') : t('Select a project')}
                className="text-sm w-full sm:w-[300px]"
                classNamePrefix="select"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '36px',
                    height: 'auto',
                    backgroundColor: 'var(--select-bg, #ffffff)',
                    borderColor: 'var(--select-border, #d1d5db)',
                    '&:hover': {
                      borderColor: 'var(--select-border-hover, #9ca3af)'
                    },
                    '.dark &': {
                      backgroundColor: 'var(--select-bg, #1e293b)',
                      borderColor: 'var(--select-border, #475569)'
                    }
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: 'var(--select-bg, #ffffff)',
                    border: '1px solid var(--select-border, #d1d5db)',
                    zIndex: 999,
                    '.dark &': {
                      backgroundColor: 'var(--select-bg, #1e293b)',
                      border: '1px solid var(--select-border, #475569)'
                    }
                  }),
                  menuList: (base) => ({
                    ...base,
                    backgroundColor: 'var(--select-bg, #ffffff)',
                    '.dark &': {
                      backgroundColor: 'var(--select-bg, #1e293b)'
                    }
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused
                      ? 'var(--select-option-hover-bg, #f3f4f6)'
                      : 'var(--select-bg, #ffffff)',
                    color: 'var(--select-text, #111827)',
                    '&:hover': {
                      backgroundColor: 'var(--select-option-hover-bg, #f3f4f6)'
                    },
                    '.dark &': {
                      backgroundColor: state.isFocused
                        ? 'var(--select-option-hover-bg, #334155)'
                        : 'var(--select-bg, #1e293b)',
                      color: 'var(--select-text, #e2e8f0)',
                      '&:hover': {
                        backgroundColor: 'var(--select-option-hover-bg, #334155)'
                      }
                    }
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: 'var(--select-text, #111827)',
                    '.dark &': {
                      color: 'var(--select-text, #e2e8f0)'
                    }
                  }),
                  multiValue: (base) => ({
                    ...base,
                    margin: '2px',
                    backgroundColor: 'var(--select-multi-bg, #e5e7eb)',
                    '.dark &': {
                      backgroundColor: 'var(--select-multi-bg, #334155)'
                    }
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: 'var(--select-text, #111827)',
                    '.dark &': {
                      color: 'var(--select-text, #e2e8f0)'
                    }
                  }),
                  input: (base) => ({
                    ...base,
                    margin: '0px',
                    color: 'var(--select-text, #111827)',
                    '.dark &': {
                      color: 'var(--select-text, #e2e8f0)'
                    }
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    backgroundColor: 'var(--select-bg, #ffffff)',
                    '.dark &': {
                      backgroundColor: 'var(--select-bg, #1e293b)'
                    }
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: 'var(--select-placeholder, #6b7280)',
                    '.dark &': {
                      color: 'var(--select-placeholder, #64748b)'
                    }
                  })
                }}
              />

              {selectedProject && (
                <>
                  {location.pathname === '/admin/questions' && (
                    <button
                      onClick={() => navigate('/admin/questions', { state: { openModal: true } })}
                      className="w-full sm:w-auto bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                    >
                      {t('Add a question')}
                    </button>
                  )}
                  {location.pathname === '/admin/sectors' && (
                    <button
                      onClick={() => navigate('/admin/sectors', { state: { openModal: true } })}
                      className="w-full sm:w-auto bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                    >
                      {t('Add a sector')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <Routes>
            <Route path="/statistics" element={<GlobalStatistics fetchWithAuth={fetchWithAuth} />} />
            <Route path="/analytics" element={<AdminDashboard />} />
            <Route
              path="/questions"
              element={
                <QuestionsSettings
                  fetchWithAuth={fetchWithAuth}
                  currentUser={currentUser}
                  selectedProject={selectedProject}
                />
              }
            />
            <Route
              path="/sectors"
              element={
                <SectorSettings
                  fetchWithAuth={fetchWithAuth}
                  currentUser={currentUser}
                  selectedProject={selectedProject}
                />
              }
            />
            <Route
              path="/duplicates"
              element={
                <AdminDuplicates
                  fetchWithAuth={fetchWithAuth}
                  selectedProject={selectedProject}
                />
              }
            />
            {/* Not in the sidebar: too heavy to compute as it stands, and it belongs to a project
                scope rather than to the general section. Reachable by URL until that move happens. */}
            <Route
              path="/users"
              element={
                <UsersStatistics fetchWithAuth={fetchWithAuth} />
              }
            />
            <Route
              path="/data-health"
              element={
                <DataHealth fetchWithAuth={fetchWithAuth} />
              }
            />
            <Route
              path="/indexation"
              element={
                <Indexation fetchWithAuth={fetchWithAuth} />
              }
            />
            {/* The journals hold every user's raw questions, so reading them is super-admin only. */}
            <Route
              path="/search-insights"
              element={currentUser.issuperadmin ? <SearchInsights fetchWithAuth={fetchWithAuth} /> : <Navigate to="/admin" />}
            />
            {/* Managing AI configurations is super-admin only; monitoring stays with admins. */}
            <Route
              path="/ai/agents"
              element={currentUser.issuperadmin ? <AgentMarketplace fetchWithAuth={fetchWithAuth} /> : <Navigate to="/admin" />}
            />
            <Route
              path="/ai/configurations"
              element={currentUser.issuperadmin ? <ConfigurationList fetchWithAuth={fetchWithAuth} /> : <Navigate to="/admin" />}
            />
            <Route
              path="/ai/configurations/:id"
              element={currentUser.issuperadmin ? <ConfigurationEditor fetchWithAuth={fetchWithAuth} /> : <Navigate to="/admin" />}
            />
            {/* Account screens moved to Connect and their routes are gone: a bookmark on one of them
                lands here rather than on an empty page. */}
            <Route path="*" element={<Navigate to="/admin/statistics" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
